import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { getStoredToken } from '../lib/authStorage';

const API = process.env.REACT_APP_BACKEND_URL || '';

const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;

const buildProtectedFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) return fileUrl;
  return `${API}${fileUrl}`;
};

export function isImageMedia(media) {
  const candidate = media?.file_url || media?.filename || media?.caption || '';
  const fileType = String(media?.file_type || media?.tipo || '').toLowerCase();
  return fileType.includes('photo') || fileType.includes('foto') || IMAGE_EXTENSIONS.test(candidate);
}

export default function ProtectedMediaThumbnail({
  media,
  alt,
  className = 'h-14 w-20',
  fallbackClassName = 'h-14 w-20',
}) {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const imageLike = isImageMedia(media);

  useEffect(() => {
    if (!imageLike || !media?.file_url) return undefined;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [imageLike, media?.file_url]);

  useEffect(() => {
    if (!shouldLoad || !imageLike || !media?.file_url) return undefined;
    let cancelled = false;
    let nextObjectUrl = '';
    setObjectUrl('');
    setFailed(false);

    const loadImage = async () => {
      try {
        const token = getStoredToken();
        const response = await fetch(buildProtectedFileUrl(media.file_url), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('Falha ao carregar miniatura');
        const blob = await response.blob();
        if (cancelled) return;
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    loadImage();
    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [imageLike, media?.file_url, shouldLoad]);

  if (!imageLike || failed) {
    return (
      <div
        ref={containerRef}
        className={`${fallbackClassName} flex flex-shrink-0 items-center justify-center rounded border bg-gray-50`}
      >
        <ImageIcon className="h-5 w-5 text-gray-500" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${className} flex flex-shrink-0 items-center justify-center overflow-hidden rounded border bg-gray-50`}
    >
      {objectUrl ? (
        <img
          src={objectUrl}
          alt={alt || media?.caption || media?.filename || 'Miniatura'}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <ImageIcon className="h-5 w-5 text-gray-400" />
      )}
    </div>
  );
}
